import React, { useCallback, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router';
import { ProjectId, SpaceId } from 'api/models';
import { Space } from 'api/space';
import { Loading } from 'components/atoms/loading';
import { SpaceIcon } from 'components/atoms/space-icon';
import { getProjectSpacesQueryKey } from 'queries';
import { useSpaceCreateMutation } from 'queries/project-spaces';
import { getSpaceDetailUrl } from 'utils/urls';
import { MenuGroupTypes } from './menu-group.types';

type MenuGroupActionTypes = Pick<MenuGroupTypes, 'project' | 'projectSpaces'>;

export const useMenugroupActions = ({
  project,
  projectSpaces = [],
}: MenuGroupActionTypes) => {
  const { projectId } = project;

  const history = useHistory();
  const queryClient = useQueryClient();
  const isNoProjectSpaces = useMemo(
    () => projectSpaces.length === 0,
    [projectSpaces],
  );
  const defaultOpenKeys = useMemo(
    () => projectSpaces.map((space) => space.spaceId.toString()),
    [projectSpaces],
  );
  const [openKeys, setOpenKeys] = React.useState(defaultOpenKeys);

  const { mutate: createSpaceMutation, isLoading: creatingSpace } =
    useSpaceCreateMutation();

  const onOpenChange = (keys) => {
    setOpenKeys(keys);
  };

  const renderIcon = useCallback(
    (_space) => {
      if (creatingSpace && _space.spaceId === 0) {
        return <Loading size="small" />;
      }

      return <SpaceIcon name={_space.spaceName} />;
    },
    [creatingSpace],
  );

  const onSpaceCreate = (newSpace: Space) => {
    if (newSpace && newSpace.spaceName) {
      createSpaceMutation(newSpace, {
        onSuccess: async (response) => {
          await queryClient.invalidateQueries(
            getProjectSpacesQueryKey(ProjectId(projectId)),
          );

          if (isNoProjectSpaces) {
            history.push(
              getSpaceDetailUrl(SpaceId(projectId, response.spaceId)),
            );
          }
        },
      });
    }
  };

  const onSpaceEditCancel = (editedSpace: Space) => {
    if (editedSpace.spaceId === 0) {
      const fiteredSpaces = projectSpaces.filter(
        (space) => space.spaceId !== 0,
      );

      return queryClient.setQueryData(
        getProjectSpacesQueryKey(ProjectId(projectId)),
        fiteredSpaces,
      );
    }

    return false;
  };

  return {
    isNoProjectSpaces,
    defaultOpenKeys,
    creatingSpace,
    openKeys,
    renderIcon,
    setOpenKeys,
    onSpaceCreate,
    onSpaceEditCancel,
    onOpenChange,
  };
};
